import React, { useState, useEffect } from 'react';
import {
  Card,
  Upload,
  Button,
  Progress,
  Table,
  Statistic,
  Row,
  Col,
  Switch,
  Select,
  message,
  Typography,
  Space,
  Divider
} from 'antd';
import {
  UploadOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  DownloadOutlined,
  ClearOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  BulbOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import './TitleProcessing.css';
import { titleProcessingApi } from '../services/api';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const TitleProcessing = () => {
  // 状态管理
  const [selectedFile, setSelectedFile] = useState(null);
  const [titleColumn, setTitleColumn] = useState('');
  const [availableColumns, setAvailableColumns] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('请上传Excel文件开始处理');
  const [previewResults, setPreviewResults] = useState([]);
  const [fullResults, setFullResults] = useState([]);
  const [processingStats, setProcessingStats] = useState(null);
  const [useApiMode, setUseApiMode] = useState(true); // 默认开启API模式
  const [currentSessionId, setCurrentSessionId] = useState(null); // 当前处理会话ID

  // 页面加载时检查是否有历史数据
  useEffect(() => {
    checkForHistoryData();
  }, []);

  // 检查历史数据
  const checkForHistoryData = async () => {
    try {
      const response = await titleProcessingApi.getHistory();
      if (response?.success && response?.data?.history?.length > 0) {
        const latestResult = response.data.history[0];
        setCurrentSessionId(latestResult.session_id);
        setStatusMessage(`检测到最近的处理结果: ${latestResult.filename}`);
        message.info('检测到历史处理数据，可以点击"恢复数据"查看');
      }
    } catch (error) {
      console.log('检查历史数据失败:', error);
    }
  };

  // 恢复历史数据
  const restoreHistoryData = async () => {
    if (!currentSessionId) {
      message.warning('没有可恢复的数据');
      return;
    }

    try {
      const response = await titleProcessingApi.restoreResult(currentSessionId);
      if (response?.success && response?.data) {
        const data = response.data;
        setFullResults(data.results || []);
        setProcessingStats(data.statistics || {});
        setProgress(100);
        setStatusMessage(`已恢复处理结果: ${data.filename}`);
        message.success('数据恢复成功');
      }
    } catch (error) {
      message.error('恢复数据失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  // 文件处理函数
  const processSelectedFile = async (file) => {
    try {
      console.log('处理选中的文件:', file?.name || '未知文件');

      if (!file) {
        message.error('未选择文件');
        return;
      }

      // 验证文件类型
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        message.error('请上传有效的Excel文件 (.xlsx 或 .xls)');
        return;
      }

      setSelectedFile(file);
      setStatusMessage('正在读取文件...');

      // 读取Excel文件获取列信息
      const arrayBuffer = await file.arrayBuffer();

      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('文件内容为空');
      }

      const workbook = XLSX.read(arrayBuffer);

      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        throw new Error('Excel文件中没有工作表');
      }

      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

      if (jsonData.length > 0 && jsonData[0]) {
        const columns = jsonData[0].filter(col => col && typeof col === 'string');
        setAvailableColumns(columns);
        console.log('检测到的列:', columns);
      } else {
        console.log('未检测到列信息，将使用第一列');
        setAvailableColumns([]);
      }

      setStatusMessage(`文件已选择: ${file.name}`);
      message.success(`文件已选择: ${file.name}`);
    } catch (error) {
      console.error('文件处理错误:', error);
      const errorMsg = error?.message || error?.toString() || '未知错误';
      message.error('读取Excel文件失败: ' + errorMsg);
      setStatusMessage('文件读取失败: ' + errorMsg);

      // 重置状态
      setSelectedFile(null);
      setAvailableColumns([]);
    }
  };

  // 简化的文件上传处理
  const handleFileUpload = (file) => {
    try {
      console.log('文件上传:', file);
      if (file) {
        processSelectedFile(file);
      }
      return false; // 阻止默认上传行为
    } catch (error) {
      console.error('文件上传处理错误:', error);
      message.error('文件上传失败: ' + (error?.message || '未知错误'));
      return false;
    }
  };

  // 预览处理结果
  const previewProcessing = async () => {
    if (!selectedFile) {
      message.warning('请先选择文件');
      return;
    }

    try {
      setStatusMessage('正在生成预览...');
      console.log('开始预览处理，使用前端本地处理');

      // 预览功能始终使用前端本地处理，不调用后端API
      await processFileLocally(selectedFile, true);
      setStatusMessage('预览生成成功');
      message.success('预览生成成功');
    } catch (error) {
      console.error('预览错误:', error);
      const errorMsg = error?.message || error?.toString() || '未知错误';
      message.error('预览失败: ' + errorMsg);
      setStatusMessage('预览失败');
    }
  };

  // 处理文件
  const processFile = async () => {
    if (!selectedFile) {
      message.warning('请先选择文件');
      return;
    }

    console.log('开始处理文件，重置进度状态');
    setIsProcessing(true);
    setProgress(0);
    setStatusMessage('正在处理文件...');

    // 确保UI更新
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      console.log('开始处理文件, API模式:', useApiMode);

      if (useApiMode) {
        // 使用API模式
        console.log('调用API处理');
        const response = await titleProcessingApi.processExcel(selectedFile, titleColumn);
        console.log('API处理响应:', response);

        if (response?.success && response?.data) {
          const data = response.data;
          setFullResults(data.results || []);
          setProcessingStats(data.statistics || {});
          setProgress(100);
          setStatusMessage(`处理完成! 共处理 ${data.processed_titles || 0} 个标题`);
          
          // 保存session_id用于数据持久化
          if (response.session_id) {
            setCurrentSessionId(response.session_id);
          }
          
          message.success('文件处理完成');
        } else {
          throw new Error('API响应格式错误');
        }
      } else {
        // 使用本地模式
        console.log('使用本地模式处理');
        await processFileLocally(selectedFile, false);
      }
    } catch (error) {
      console.error('处理错误:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || error?.toString() || '未知错误';
      message.error('处理文件失败: ' + errorMsg);
      setStatusMessage('处理失败，尝试本地模式');

      // API失败时自动切换到本地模式
      if (useApiMode) {
        console.log('API失败，切换到本地模式');
        try {
          await processFileLocally(selectedFile, false);
          message.success('已切换到本地模式处理');
        } catch (localError) {
          console.error('本地处理也失败:', localError);
          const localErrorMsg = localError?.message || localError?.toString() || '本地处理失败';
          message.error('本地处理失败: ' + localErrorMsg);
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // 本地处理文件（前端模式）
  const processFileLocally = async (file, isPreview = false) => {
    try {
      console.log('本地处理文件:', file.name, '预览模式:', isPreview);

      if (!isPreview) {
        setProgress(5);
        setStatusMessage('正在读取Excel文件...');
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer);
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

      if (!isPreview) {
        setProgress(10);
        setStatusMessage('正在提取标题数据...');
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // 提取标题数据
      const columnIndex = titleColumn ?
        jsonData[0].indexOf(titleColumn) : 0;

      const titles = jsonData.slice(1)
        .map(row => row[columnIndex])
        .filter(title => title && typeof title === 'string');

      console.log('提取的标题数量:', titles.length);

      if (!isPreview) {
        setProgress(15);
        setStatusMessage(`开始处理 ${titles.length} 个标题...`);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // 模拟处理过程
      const results = [];
      const total = isPreview ? Math.min(titles.length, 10) : titles.length;

      for (let i = 0; i < total; i++) {
        const title = titles[i];
        const processed = processTitle(title);

        results.push({
          original: title,
          suggested: processed.suggested,
          removed_count: processed.removedCount,
          kept_count: processed.keptCount,
          has_issue: processed.hasIssue,
          redundant_words: processed.redundantWords,
          status: processed.hasIssue ? '需要优化' : '符合要求'
        });

        if (!isPreview) {
          const progressPercent = Math.round((i + 1) / total * 100);
          setProgress(progressPercent);
          setStatusMessage(`处理中: ${i + 1}/${total} 个标题 (${progressPercent}%)`);

          // 每处理10个标题或到达重要节点时更新UI
          if ((i + 1) % 10 === 0 || i === total - 1 || progressPercent % 10 === 0) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        }
      }

      if (isPreview) {
        setPreviewResults(results);
        console.log('预览结果:', results);
      } else {
        setFullResults(results);
        const issueCount = results.filter(r => r.has_issue).length;
        const totalRemoved = results.reduce((sum, r) => sum + r.removed_count, 0);

        const stats = {
          total_processed: results.length,
          optimized_titles: issueCount,
          character_limited_titles: totalRemoved,
          optimization_rate: `${(issueCount / results.length * 100).toFixed(1)}%`
        };

        setProcessingStats(stats);
        setProgress(100);
        setStatusMessage(`处理完成! 共处理 ${results.length} 个标题`);
        console.log('处理完成，统计:', stats);
      }

    } catch (error) {
      console.error('本地处理错误:', error);
      throw new Error('本地处理失败: ' + (error?.message || error?.toString() || '未知错误'));
    }
  };

  // 完整的标题处理逻辑（前端版本）- 与HTML原版完全一致
  const processTitle = (title) => {
    try {
      // 多语言介词列表（与HTML版本完全一致）
      const prepositions = {
        'english': ['a', 'an', 'the', 'and', 'or', 'for', 'as', 'but', 'at', 'by', 'from', 'in', 'into', 'of', 'on', 'to', 'with'],
        'german': ['ab', 'am', 'an', 'ans', 'auf', 'aus', 'bei', 'bis', 'durch', 'für', 'gegen', 'hinter', 'in', 'mit', 'nach', 'neben', 'über', 'um', 'unter', 'von', 'vor', 'zu', 'zum'],
        'french': ['à', 'au', 'aux', 'avec', 'dans', 'de', 'des', 'du', 'en', 'par', 'pour', 'sur', 'sous', 'vers', 'chez'],
        'italian': ['a', 'al', 'allo', 'ai', 'agli', 'all', 'agl', 'alla', 'alle', 'con', 'da', 'dal', 'dallo', 'dai', 'dagli', 'dall', 'dagl', 'dalla', 'dalle', 'di', 'del', 'dello', 'dei', 'degli', 'dell', 'degl', 'della', 'delle', 'in', 'nel', 'nello', 'nei', 'negli', 'nell', 'negl', 'nella', 'nelle']
      };

      // 创建所有有效介词的集合
      const allPrepositions = new Set();
      Object.values(prepositions).forEach(langPrepositions => {
        langPrepositions.forEach(p => allPrepositions.add(p));
      });

      // 提取单词词根（与HTML版本完全一致）
      const extractWordRoots = (word) => {
        const cleanedWord = word.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '').toLowerCase();
        const parts = cleanedWord.split('-');
        const roots = new Set([cleanedWord]);
        parts.forEach(part => {
          if (part) roots.add(part);
        });
        return Array.from(roots);
      };

      // 初始化结果对象
      const result = {
        originalTitle: title,
        suggestedTitle: title,
        hasIssue: false,
        issues: [],
        keptCount: 0,
        removedCount: 0,
        redundantWords: []
      };

      const words = title.split(/\s+/).filter(w => w.trim().length > 0);
      if (words.length === 0) {
        return {
          suggested: title,
          removedCount: 0,
          keptCount: 0,
          hasIssue: false,
          redundantWords: []
        };
      }

      const wordCount = {};
      const rootOccurrences = {};

      // 遍历每个单词，提取词根（与HTML版本完全一致）
      words.forEach((word, idx) => {
        const wordRoots = extractWordRoots(word);

        // 排除介词
        const isPreposition = wordRoots.some(root => allPrepositions.has(root));
        if (isPreposition) return;

        // 记录词根出现
        wordRoots.forEach(root => {
          if (!rootOccurrences[root]) rootOccurrences[root] = [];
          rootOccurrences[root].push({ index: idx, originalWord: word });

          wordCount[root] = (wordCount[root] || 0) + 1;
        });
      });

      // 识别冗余问题并构建删除索引（与HTML版本完全一致）
      const toRemove = new Set();
      for (const [root, count] of Object.entries(wordCount)) {
        if (count >= 3) {
          const positions = rootOccurrences[root];

          result.hasIssue = true;
          result.issues.push({
            word: root,
            count: count,
            indices: positions.map(p => p.index)
          });
          result.redundantWords.push(root);

          // 核心删除逻辑：保留第一个和最后一个，删除中间所有位置
          for (let i = 1; i < positions.length - 1; i++) {
            toRemove.add(positions[i].index);
          }
        }
      }

      // 构建修改后的标题
      const newWords = words.filter((_, idx) => !toRemove.has(idx));
      result.removedCount = toRemove.size;
      result.keptCount = newWords.length;

      // 生成新标题（与HTML版本完全一致）
      result.suggestedTitle = newWords.join(' ')
        .replace(/\s+([,.!?;:])/g, '$1')
        .replace(/\s+/g, ' ')
        .trim();

      return {
        suggested: result.suggestedTitle,
        removedCount: result.removedCount,
        keptCount: result.keptCount,
        hasIssue: result.hasIssue,
        redundantWords: result.redundantWords
      };

    } catch (error) {
      console.error('标题处理错误:', error);
      return {
        suggested: title,
        removedCount: 0,
        keptCount: title.split(/\s+/).length,
        hasIssue: false,
        redundantWords: [],
        error: error.message
      };
    }
  };

  // 下载结果
  const downloadResults = async () => {
    if (fullResults.length === 0) {
      message.warning('没有可下载的结果');
      return;
    }

    try {
      console.log('开始下载结果, API模式:', useApiMode);

      if (useApiMode) {
        // 使用API下载
        const filename = `title_processing_results_${new Date().toISOString().slice(0, 10)}.xlsx`;
        console.log('调用API下载:', filename);

        const response = await titleProcessingApi.exportResults(fullResults, filename);
        console.log('API下载响应:', response);

        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        // 本地下载
        console.log('使用本地下载');
        const ws = XLSX.utils.json_to_sheet(fullResults.map(result => ({
          '原始标题': result.original,
          '优化后标题': result.suggested,
          '删除词数': result.removed_count,
          '保留词数': result.kept_count,
          '冗余词汇': result.redundant_words.join(', '),
          '状态': result.status
        })));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '标题处理结果');
        const filename = `title_processing_results_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, filename);
      }

      message.success('结果已下载');
    } catch (error) {
      console.error('下载错误:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || error?.toString() || '未知错误';
      message.error('下载失败: ' + errorMsg);

      // API失败时自动切换到本地下载
      if (useApiMode) {
        console.log('API下载失败，切换到本地下载');
        try {
          const ws = XLSX.utils.json_to_sheet(fullResults.map(result => ({
            '原始标题': result.original,
            '优化后标题': result.suggested,
            '删除词数': result.removed_count,
            '保留词数': result.kept_count,
            '冗余词汇': result.redundant_words.join(', '),
            '状态': result.status
          })));

          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, '标题处理结果');
          const filename = `title_processing_results_${new Date().toISOString().slice(0, 10)}.xlsx`;
          XLSX.writeFile(wb, filename);
          message.success('已切换到本地下载');
        } catch (localError) {
          console.error('本地下载也失败:', localError);
          const localErrorMsg = localError?.message || localError?.toString() || '本地下载失败';
          message.error('本地下载失败: ' + localErrorMsg);
        }
      }
    }
  };

  // 清空所有内容
  const clearAll = () => {
    console.log('清空所有内容');
    setSelectedFile(null);
    setTitleColumn('');
    setAvailableColumns([]);
    setPreviewResults([]);
    setFullResults([]);
    setProcessingStats(null);
    setProgress(0);
    setStatusMessage('请上传Excel文件开始处理');
    setIsProcessing(false);
  };

  // 表格列配置
  const previewColumns = [
    {
      title: '原始标题',
      dataIndex: 'original',
      key: 'original',
      width: '30%',
      ellipsis: true,
    },
    {
      title: '优化后标题',
      dataIndex: 'suggested',
      key: 'suggested',
      width: '30%',
      ellipsis: true,
      render: (text) => <Text type="success">{text}</Text>
    },
    {
      title: '删除词数',
      dataIndex: 'removed_count',
      key: 'removed_count',
      width: '10%',
      align: 'center',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (status) => (
        <Text type={status === '需要优化' ? 'warning' : 'success'}>
          {status}
        </Text>
      )
    },
    {
      title: '冗余词汇',
      dataIndex: 'redundant_words',
      key: 'redundant_words',
      width: '15%',
      render: (words) => Array.isArray(words) ? words.join(', ') : words
    }
  ];

  return (
    <div className="title-processing-container">
      {/* 页面头部 */}
      <div className="page-header">
        <Title level={1}>
          <FileExcelOutlined /> ￥亚马逊标题冗余处理工具
        </Title>
        <Paragraph>高效处理标题冗余词，支持Excel批量处理</Paragraph>
      </div>

      {/* API模式切换 - 已隐藏，默认使用API模式 */}
      {/*
      <Card className="api-mode-card">
        <div className="api-toggle">
          <Text strong>处理模式：</Text>
          <Switch 
            checked={useApiMode}
            onChange={(checked) => {
              console.log('切换处理模式:', checked ? 'API模式' : '本地模式');
              setUseApiMode(checked);
            }}
            checkedChildren="API模式"
            unCheckedChildren="本地模式"
          />
          <Text type="secondary">
            {useApiMode ? '使用后端API处理（推荐）' : '使用前端本地处理'}
            <span style={{ marginLeft: 8, fontSize: '12px', color: '#999' }}>
              (当前: {useApiMode ? 'API模式' : '本地模式'})
            </span>
          </Text>
        </div>
      </Card>
      */}

      {/* 文件上传卡片 */}
      <Card title={<><FileExcelOutlined /> 上传Excel文件</>} className="upload-card">
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            <InfoCircleOutlined /> 文件要求：仅支持.xlsx或.xls格式，标题需要放在指定列中
          </Text>
        </div>

        <div style={{ textAlign: 'center' }}>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleFileUpload(file);
              }
            }}
            style={{ display: 'none' }}
            id="file-input"
          />
          <Button
            icon={<UploadOutlined />}
            size="large"
            style={{ width: '100%', height: '120px', fontSize: '16px' }}
            onClick={() => document.getElementById('file-input').click()}
          >
            <div>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                <UploadOutlined />
              </div>
              <div>点击选择Excel文件</div>
              <div style={{ fontSize: '12px', color: '#999' }}>支持 .xlsx 和 .xls 格式</div>
            </div>
          </Button>
        </div>

        {selectedFile && (
          <div className="file-info" style={{ marginTop: 16, padding: 12, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
            <FileExcelOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            <span style={{ fontWeight: 'bold' }}>{selectedFile.name}</span>
            <span className="file-size" style={{ marginLeft: 8, color: '#666' }}>
              ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </span>
          </div>
        )}

        {availableColumns.length > 0 && (
          <div className="column-selection" style={{ marginTop: 16 }}>
            <label style={{ marginRight: 8 }}>选择标题列：</label>
            <Select
              value={titleColumn}
              onChange={setTitleColumn}
              placeholder="自动检测（使用第一列）"
              style={{ width: 200 }}
              allowClear
            >
              {availableColumns.map(col => (
                <Option key={col} value={col}>{col}</Option>
              ))}
            </Select>
          </div>
        )}

        <div className="controls" style={{ marginTop: 20 }}>
          <Space>
            <Button
              type="primary"
              icon={<EyeOutlined />}
              disabled={!selectedFile || isProcessing}
              onClick={previewProcessing}
            >
              预览处理结果
            </Button>

            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              disabled={!selectedFile || isProcessing}
              onClick={processFile}
              loading={isProcessing}
            >
              {isProcessing ? '处理中...' : '开始处理'}
            </Button>

            <Button
              icon={<ClearOutlined />}
              onClick={clearAll}
            >
              清除
            </Button>

            {currentSessionId && (
              <Button
                type="default"
                icon={<EyeOutlined />}
                onClick={restoreHistoryData}
                style={{ marginLeft: 8 }}
              >
                恢复数据
              </Button>
            )}
          </Space>
        </div>
      </Card>

      {/* 状态消息 */}
      <Card className="status-card">
        <Text>{statusMessage}</Text>
      </Card>

      {/* 进度显示 */}
      {(isProcessing || progress > 0) && (
        <Card className="progress-card">
          <Progress
            percent={progress}
            status={isProcessing ? "active" : "success"}
            showInfo={true}
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
          />
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Text type="secondary">{statusMessage}</Text>
          </div>
        </Card>
      )}

      {/* 预览结果 */}
      {previewResults.length > 0 && (
        <Card
          title={<><EyeOutlined /> 预览结果 (前{previewResults.length}条)</>}
          className="preview-card"
        >
          <Table
            dataSource={previewResults}
            columns={previewColumns}
            pagination={false}
            size="small"
            rowKey={(record, index) => index}
          />
        </Card>
      )}

      {/* 处理结果统计 */}
      {processingStats && (
        <Card
          title={<><InfoCircleOutlined /> 处理统计</>}
          className="stats-card"
        >
          <Row gutter={16}>
            <Col span={6}>
              <Statistic title="总标题数" value={processingStats?.total_processed || 0} />
            </Col>
            <Col span={6}>
              <Statistic title="需要优化" value={processingStats?.optimized_titles || 0} />
            </Col>
            <Col span={6}>
              <Statistic title="字符限制" value={processingStats?.character_limited_titles || 0} />
            </Col>
            <Col span={6}>
              <Statistic title="优化率" value={processingStats?.optimization_rate || '0.0%'} />
            </Col>
          </Row>

          <Divider />

          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="large"
            onClick={downloadResults}
            disabled={fullResults.length === 0}
          >
            下载处理结果
          </Button>
        </Card>
      )}

      {/* 功能说明 */}
      <Card
        title={<><BulbOutlined /> 删除机制说明</>}
        className="explanation-card"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <div className="feature">
              <div className="feature-icon">🔍</div>
              <div>
                <Text strong>词根检测算法</Text>
                <Paragraph type="secondary">智能识别带连字符单词中的词根</Paragraph>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="feature">
              <div className="feature-icon">🔀</div>
              <div>
                <Text strong>首尾保留策略</Text>
                <Paragraph type="secondary">重复≥3次的单词保留首尾，删除中间</Paragraph>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="feature">
              <div className="feature-icon">✨</div>
              <div>
                <Text strong>结果优化</Text>
                <Paragraph type="secondary">自动修正多余空格和标点符号</Paragraph>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="feature">
              <div className="feature-icon">🌍</div>
              <div>
                <Text strong>多语言支持</Text>
                <Paragraph type="secondary">支持英语、德语、法语、意大利语</Paragraph>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="feature">
              <div className="feature-icon">🎯</div>
              <div>
                <Text strong>智能字符处理</Text>
                <Paragraph type="secondary">字符智能优化保留关键信息</Paragraph>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default TitleProcessing;